import { cloneDeep } from 'lodash';
import { commits, dispatches, getters } from '@/modules/core/app/helpers/store';
import { shallowCopyKeyedObject } from '@/modules/core/helper';
import { WidgetEvent, WidgetState } from '@/modules/ta/widget/widget.constants';
import EventBus from '@/modules/core/app/helpers/EventBus';

export const mutations = {
  setWidgetInLayout(state, { layoutId, widget }) {
    state.layouts[layoutId].widgets[widget.id] = widget;
    dispatches.widget.updateWidget(widget);
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  removeWidgetInLayout(state, { layoutId, widget }) {
    if (Object.hasOwn(state.layouts[layoutId].widgets, widget.id)) {
      delete state.layouts[layoutId].widgets[widget.id];
    }
    commits.widget.removeWidget({ widget });
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  setLayoutsFromDashboard(state, { dashboard }) {
    commits.widget.setWidgetsFromDashboard({ dashboard });

    state.layouts = {};
    Object.values(dashboard.layouts).forEach((layout) => {
      if (layout?.widgets) {
        layout.widgets = Object.values(layout.widgets).reduce((accum, widget) => {
          accum[widget.id] = widget;
          return accum;
        }, {});
        state.layouts[layout.id] = layout;
      }
    });

    // signal to update layout when widgets are updated in a layout
    setTimeout(() => {
      EventBus.signal(WidgetEvent.UPDATE_DISPLAY_ORDER, true);
    }, 250);
  },
  saveList(state, layouts) {
    layouts.forEach((layout) => {
      layout.widgets = getters.dashboardLayout
        .getWidgetsInLayout(layout.id)
        .reduce((accum, { id }) => {
          accum[id] = null;
          return accum;
        }, {});
      state.layouts[layout.id] = layout;
    });
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  setLayout(state, layout) {
    layout.widgets = Object.keys(layout.widgets || []).reduce((accum, widgetId) => {
      accum[widgetId] = null;
      return accum;
    }, {});
    state.layouts[layout.id] = layout;
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  updateTitleAndOrder(layoutState, updatedLayouts) {
    updatedLayouts.forEach((layout) => {
      const updatedLayout = layoutState.layouts[layout.id];
      updatedLayout.display_order = layout.display_order;
      updatedLayout.title = layout.title;
    });
  },
  clearChartPaletteFromWidgets() {
    const widgets = [];
    getters.dashboard.getLayoutsInDashboard().forEach((layout) => {
      getters.dashboardLayout.getWidgetsInLayout(layout.id).forEach((widget) => {
        widget.metadata.chart_palette = null;
        widgets.push(widget);
      });
    });

    commits.widget.updateWidgets(widgets);
  },
  addGhostWidget(layoutState, { layoutId, widget, state = WidgetState.STATE_DONE }) {
    layoutState.layouts[layoutId].widgets[widget.id] = widget;

    commits.widget.setWidget({ widget, state });
    commits.widgetBuilder.setBuilderForm({ widget });

    commits.dashboardLayout.addWidget({
      layoutId: layoutId || widget.layout_id,
      widget,
    });
  },
  addWidget(
    layoutState,
    { layoutId, widget, state = WidgetState.STATE_DONE, isDuplicated = false }
  ) {
    if (!layoutState.layouts || !layoutState.layouts[layoutId]) {
      layoutState.layouts[layoutId] = {
        widgets: {},
      };
    }
    layoutState.layouts[layoutId].widgets[widget.id] = widget;
    commits.widget.setWidget({ widget, state });
    layoutState.layouts[layoutId].widgets = shallowCopyKeyedObject(
      layoutState.layouts[layoutId].widgets
    );

    if (isDuplicated) {
      EventBus.signal(WidgetEvent.DUPLICATED, widget);
    }
  },
  /**
   * @param {LayoutState} state
   * @param map
   */
  setOriginalLayoutWidgets(state, widgets) {
    state.originalLayoutWidgets = cloneDeep(widgets);
  },

  removeLayout(state, layoutId) {
    const layout = state.layouts[layoutId];
    const widgetIds = Object.keys(layout.widgets);

    commits.widget.removeWidgetsFromId({ widgetIds });
    delete state.layouts[layoutId];
    state.layouts = shallowCopyKeyedObject(state.layouts);
  },
  setExecutiveSummaryOnLayout(state, executiveSummary) {
    state.layouts[executiveSummary.layout_id].executive_summary = executiveSummary;
  },
  setViewingLayoutPreview(state, value) {
    state.viewingLayoutPreview = value;
  },
};
